/** Llamadas HTTP del feature OrdenCorte (gestión de morosidad). */
import { apiDownload, apiRequest } from "../../lib/api";

export type EstadoOrdenCorte =
  | "Pendiente"
  | "Notificada"
  | "EnRuta"
  | "Cortada"
  | "Repuesta"
  | "Anulada";

export interface OrdenCorteRead {
  id: number;
  empresa_id: number;
  contrato_id: number;
  fecha_generacion: string;
  fecha_notificacion: string | null;
  fecha_corte_programada: string | null;
  fecha_corte_realizada: string | null;
  fecha_reposicion: string | null;
  estado: EstadoOrdenCorte;
  monto_deuda_al_corte: number;
  costo_corte: number;
  costo_reposicion: number;
  numero_notificacion: string | null;
  articulo_ley: string;
  observaciones: string | null;
  motivo_anulacion: string | null;
  created_by_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface AbrirOrdenPayload {
  contrato_id: number;
  monto_deuda_al_corte: number;
  fecha_corte_programada?: string | null;
  observaciones?: string | null;
}

/** El N° de notificación lo asigna el backend (correlativo del comité). */
export interface NotificarPayload {
  fecha_notificacion: string;
  articulo_ley?: string | null;
}

export interface CortarPayload {
  fecha_corte_realizada?: string | null;
  costo_corte: number;
}

export interface ReponerPayload {
  fecha_reposicion?: string | null;
  costo_reposicion: number;
  /** Por qué se repone con deuda. Queda en la bitácora. */
  observacion?: string | null;
}

export function listarActivas(estado?: EstadoOrdenCorte): Promise<OrdenCorteRead[]> {
  const qs = estado ? `?estado=${estado}` : "";
  return apiRequest<OrdenCorteRead[]>(`/api/v1/ordenes-corte${qs}`);
}

export function listarPorContrato(
  contratoId: number,
  incluirAnuladas = false,
): Promise<OrdenCorteRead[]> {
  const qs = incluirAnuladas ? "?incluir_anuladas=true" : "";
  return apiRequest<OrdenCorteRead[]>(
    `/api/v1/ordenes-corte/contrato/${contratoId}${qs}`,
  );
}

export function obtenerOrden(id: number): Promise<OrdenCorteRead> {
  return apiRequest<OrdenCorteRead>(`/api/v1/ordenes-corte/${id}`);
}

export function abrirOrden(payload: AbrirOrdenPayload): Promise<OrdenCorteRead> {
  return apiRequest<OrdenCorteRead>("/api/v1/ordenes-corte", {
    method: "POST",
    body: payload,
  });
}

export function notificarOrden(
  id: number,
  payload: NotificarPayload,
): Promise<OrdenCorteRead> {
  return apiRequest<OrdenCorteRead>(`/api/v1/ordenes-corte/${id}/notificar`, {
    method: "POST",
    body: payload,
  });
}

export function marcarEnRuta(id: number): Promise<OrdenCorteRead> {
  return apiRequest<OrdenCorteRead>(`/api/v1/ordenes-corte/${id}/en-ruta`, {
    method: "POST",
  });
}

export function cortarOrden(
  id: number,
  payload: CortarPayload,
): Promise<OrdenCorteRead> {
  return apiRequest<OrdenCorteRead>(`/api/v1/ordenes-corte/${id}/cortar`, {
    method: "POST",
    body: payload,
  });
}

export function reponerOrden(
  id: number,
  payload: ReponerPayload,
): Promise<OrdenCorteRead> {
  return apiRequest<OrdenCorteRead>(`/api/v1/ordenes-corte/${id}/reponer`, {
    method: "POST",
    body: payload,
  });
}

/** Aviso de corte en PDF para entregar al socio (queda auditado). */
export function descargarAvisoCorte(id: number): Promise<void> {
  return apiDownload(
    `/api/v1/ordenes-corte/${id}/aviso.pdf`,
    `aviso_corte_${String(id).padStart(6, "0")}.pdf`,
  );
}

export function anularOrden(id: number, motivo: string): Promise<OrdenCorteRead> {
  return apiRequest<OrdenCorteRead>(`/api/v1/ordenes-corte/${id}/anular`, {
    method: "POST",
    body: { motivo },
  });
}

export interface MarcarVencidasResponse {
  afectadas: number;
  fecha_corte: string;
}

export function marcarVencidas(): Promise<MarcarVencidasResponse> {
  return apiRequest<MarcarVencidasResponse>(
    "/api/v1/lecturas/marcar-vencidas",
    { method: "POST" },
  );
}
